/**
 * Player trails — where each player has come from over the last few seconds
 * (founder ask, 2026-08-27).
 *
 * Reads the frame buffer the canvas controller already holds, so like the
 * heatmap and the ball trail this costs no fetch.
 *
 * The one design decision worth knowing: when players are highlighted, ONLY
 * those are trailed. Twenty-two simultaneous trails is a plate of spaghetti
 * that answers no question; one runner's last five seconds answers a real
 * one. The page already maintains `highlightedPlayers` (moment selection,
 * player search), so this follows a selection that already exists rather than
 * inventing a second one.
 */
import { PLAYER_SPEED, fraction } from './speedRamp'
import type { TrackingFrame } from './trackingLoader'

/** 5s at 30fps. Shorter than the ball trail: 22 of them at 10s is soup. */
export const TRAIL_FRAMES = 150

/**
 * Speed scale, in metres per second, taken from this game's actual
 * distribution rather than 0..max: p50 1.0, p90 3.4, p99 5.8, max 9.5.
 *
 * Below SPEED_MIN a player is standing or walking and the trail should barely
 * register — most of a match is spent there, and scaling from zero would make
 * the whole pitch look busy. SPEED_MAX sits near the 99th percentile so a real
 * sprint saturates rather than only the single fastest moment of the match.
 */
export const SPEED_MIN = PLAYER_SPEED.min
export const SPEED_MAX = PLAYER_SPEED.max

/** 0..1 across the player speed range, or null when the feed has no speed.
 *  Thin wrapper so callers do not each repeat which range players use. */
export function speedFraction(speed: number | undefined | null): number | null {
  return fraction(speed, PLAYER_SPEED)
}

export interface TrailPoint {
  x: number
  y: number
  /** 0 = oldest point in the window, 1 = the playhead. */
  t: number
  /** Normalized speed 0..1, or null when the feed carries none. */
  s: number | null
}
export interface PlayerTrail { key: string; team: number; jersey: number; points: TrailPoint[] }

export interface BuildOptions {
  trailFrames?: number
  /** "team_jersey" keys (e.g. "0_16"). Empty/undefined = every player. */
  only?: string[]
}

/**
 * Group buffer positions into one polyline per player.
 *
 * Pure and exported so the grouping is tested without a canvas: `t` is
 * normalized 0..1 from oldest to newest so the renderer can fade by age
 * without knowing the window length.
 */
export function buildTrails(
  frames: TrackingFrame[],
  endIndex: number,
  { trailFrames = TRAIL_FRAMES, only }: BuildOptions = {},
): PlayerTrail[] {
  if (!frames.length) return []
  const end = Math.min(endIndex, frames.length - 1)
  const start = Math.max(0, end - trailFrames + 1)
  const span = Math.max(1, end - start)
  const wanted = only && only.length ? new Set(only) : null

  const byPlayer = new Map<string, PlayerTrail>()
  for (let i = start; i <= end; i++) {
    const players = frames[i]?.players
    if (!players) continue
    for (const p of players) {
      // Referees are team 3/4 in this feed; they are not players.
      if (p.team !== 0 && p.team !== 1) continue
      if (p.jersey == null) continue
      if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue
      const key = `${p.team}_${p.jersey}`
      if (wanted && !wanted.has(key)) continue
      let trail = byPlayer.get(key)
      if (!trail) {
        trail = { key, team: p.team, jersey: p.jersey, points: [] }
        byPlayer.set(key, trail)
      }
      trail.points.push({ x: p.x, y: p.y, t: (i - start) / span, s: speedFraction(p.speed) })
    }
  }
  return [...byPlayer.values()]
}

interface PlayerTrailsOptions {
  getCanvasState: () => {
    ctx: CanvasRenderingContext2D | null
    frames: TrackingFrame[]
    currentFrameIndex: number
    highlightedPlayers: string[]
  }
  convertToCanvasCoords: (x: number, y: number) => { x: number; y: number }
  getTeamColors: () => Record<number, string>
}

export class PlayerTrailsManager {
  enabled = false
  config = { trailFrames: TRAIL_FRAMES, width: 2 }

  private opts: PlayerTrailsOptions

  constructor(opts: PlayerTrailsOptions) {
    this.opts = opts
  }

  draw(): void {
    if (!this.enabled) return
    const { ctx, frames, currentFrameIndex, highlightedPlayers } = this.opts.getCanvasState()
    if (!ctx || !frames.length) return

    const trails = buildTrails(frames, currentFrameIndex, {
      trailFrames: this.config.trailFrames,
      only: highlightedPlayers,
    })
    if (!trails.length) return

    const colors = this.opts.getTeamColors()
    ctx.save()
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    for (const trail of trails) {
      ctx.strokeStyle = colors[trail.team] || '#999'
      ctx.lineWidth = this.config.width
      // Segment by segment rather than one path: the fade is per-segment, and
      // a single stroke can only carry one alpha.
      for (let i = 1; i < trail.points.length; i++) {
        const p0 = trail.points[i - 1]
        const p1 = trail.points[i]
        if (p0 === undefined || p1 === undefined) continue
        const a = this.opts.convertToCanvasCoords(p0.x, p0.y)
        const b = this.opts.convertToCanvasCoords(p1.x, p1.y)
        ctx.globalAlpha = 0.08 + 0.62 * p1.t
        ctx.beginPath()
        ctx.moveTo(a.x, a.y)
        ctx.lineTo(b.x, b.y)
        ctx.stroke()
      }
    }
    ctx.restore()
  }
}
